<template>
  <div>
    <div class="container">
      <div v-if="serverError" class="alert alert-danger" role="alert">
        {{ serverError }}
      </div>
      <form @submit.prevent class="editProjectForm needs-validation" novalidate>
        <div class="card mb-2">
          <div class="card-header bg-primary text-white">
            Edit Project Details
          </div>
          <div class="card-body">
            <LoadingSpinner v-if="loading"
              message ='Loading Project Details' size = '1x'>
            </LoadingSpinner>
            <LoadingSpinner v-if="submitting || uploading"
              message ='Submitting Project Details' size = '1x'>
            </LoadingSpinner>
            <div class="form-group row" v-if="!loading && !submitting && !uploading">
              <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                <DetailsRow label="Project ID" :field="project.id" />
                <div class="row py-1">
                  <label for="projectStatus" class="col-4 col-form-label label-text">
                    <span class="text-danger">*</span>Project Status
                  </label>
                  <div class="loading col-lg-5" v-if="projectStatusLoading">
                    Loading...
                  </div>
                  <div class="col-8" v-if="!projectStatusLoading">
                    <select v-validate="'required'"
                            data-vv-as="Project Status"
                            class="form-control"
                            name="projectStatus"
                            v-model="project.statusId">
                      <option v-for="option in projectStatusOptions"
                              :key="option.id"
                              :value="option.id">
                        {{ option.name }}
                      </option>
                    </select>
                    <div class="invalid-feedback">{{ errors.first('projectStatus') }}</div>
                  </div>
                </div>
                <div class="row py-1">
                  <label for="projectName" class="col-4 col-form-label label-text">
                    <span class="text-danger">*</span>Project Name
                  </label>
                  <div class="col-8">
                    <input v-validate="'required'"
                      data-vv-as="Project Name"
                      type="text"
                      class="form-control"
                      name="projectName"
                      v-model="project.name"
                      placeholder="Enter Project Name"
                      maxlength="50" />
                    <small v-if="!errors.first('projectName')" class="form-text text-muted">
                      Max 50 characters
                    </small>
                      <div class="invalid-feedback">{{ errors.first('projectName') }}</div>
                  </div>
                </div>
                <DetailsRow label="Created By"
                  :field="project.createdByName"
                />
                <DetailsRow label="Created Date"
                  :field="project.createdAt | dateString" />
                <DetailsRow label="Last Edited By"
                  :field="project.lastEditedByName"
                />
                <DetailsRow label="Last Edited"
                  :field="project.lastEditedAt | dateTimeString" />
                <div class="row py-1">
                  <label for="projectDescription" class="col-4 col-form-label label-text">
                    Project Description
                  </label>
                  <div class="col-8">
                    <textarea data-vv-as="Project Description"
                          type="textarea"
                          class="form-control"
                          name="projectDescription"
                          v-validate="'max:1000'"
                          v-model="project.description"
                          placeholder="Enter Project Description"
                          rows="2"
                          maxlength="1000" />
                    <small v-if="!errors.first('projectDescription')" class="form-text text-muted">
                      Max 1000 characters
                    </small>
                    <div class="invalid-feedback">{{ errors.first('projectDescription') }}</div>
                  </div>
              </div>
              </div>
              <div class="col-6 d-none d-xs-none d-sm-none d-md-block">
                <div class="row">
                  <croppa v-model="summaryFileCroppa"
                    :placeholder="'Click or drag here to add a photo'"
                    :placeholder-font-size="15"
                    :width="490"
                    :height="330"
                    :disable-drag-to-move="true"
                    :disable-scroll-to-zoom="true"
                    :disable-rotation="true"
                    :remove-button-color="'#853813'"
                    :remove-button-size="30"
                    :replace-drop="true"
                    @file-choose="handleFileChoose"
                    :accept="'image/*'"
                  >
                    <img v-if="projectPhoto && projectPhoto.length > 30"
                     slot="initial" :src="projectPhoto" />
                  </croppa>
                </div>
              </div>
            </div>
          </div>
          <div class='card-footer'>
          <b-button variant="danger" class="button-row-spacing" v-b-modal="'cancelConfirmation'">
            Cancel
          </b-button>
          <b-button @click="onSubmit">Save</b-button>
        </div>
        </div>
      </form>
    </div>
    <CancelModal :modalId="'cancelConfirmation'" @cancelled="cancelConfirmed"></CancelModal>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import DetailsRow from '@/components/DetailsRow.vue';
import CancelModal from '../components/CancelModal.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import { LookupModules } from '@/store/moduleConfig';

function initialState() {
  return {
    summaryFileCroppa: null,
    needsPhotoUpload: false,
  };
}

export default {
  data() {
    return initialState();
  },
  created() {
    this.$store.dispatch(`${LookupModules.PROJECT_STATUSES.namespace}/fetchOptions`,
      LookupModules.PROJECT_STATUSES.url);
    this.$store.dispatch('CurrentProject/fetch', { projectId: parseInt(this.$route.params.project_id, 10) });
  },
  computed: {
    ...mapState(LookupModules.PROJECT_STATUSES.namespace, {
      projectStatusLoading: state => state.loading,
    }),
    ...mapState('CurrentProject', {
      project: state => state.currentProject,
      projectPhoto: state => state.projectPhoto,
      loading: state => state.loading,
      serverError: state => state.serverError,
      uploading: state => state.uploading,
      submitting: state => state.submitting,
    }),
    ...mapGetters(LookupModules.PROJECT_STATUSES.namespace, {
      projectStatusOptions: 'getActiveOptions',
    }),
  },
  components: {
    DetailsRow,
    CancelModal,
    LoadingSpinner,
  },
  mounted() {
  },
  methods: {
    handleFileChoose() {
      this.needsPhotoUpload = true;
    },
    getButtonText() {
      return !this.project.photoBoxId ? 'Upload Photo' : 'Update Photo';
    },
    getImageFromProject() {
      return this.project.photoBoxId ? `data:image/png;base64, ${this.project.photoBase64Str}` : '';
    },
    cancelConfirmed() {
      this.$router.push({
        name: 'project',
        params: { project_id: parseInt(this.$route.params.project_id, 10) },
      });
    },
    onSubmit() {
      this.$validator.validate()
        .then((result) => {
          if (result) {
            this.$store.dispatch('CurrentProject/updateEntity', {
              project: this.project,
              submittedCallback: this.needsPhotoUpload ? null : this.routeToCurrentProject,
            }).then(() => {
              if (this.needsPhotoUpload) {
                const photoSummary = this.summaryFileCroppa && this.summaryFileCroppa.hasImage()
                  ? this.summaryFileCroppa.getChosenFile() : null;
                this.$store.dispatch('CurrentProject/uploadPhoto', {
                  photoSummary,
                  projectId: this.project.id,
                  submittedCallback: this.routeToCurrentProject,
                });
              }
            });
          }
        });
    },
    routeToCurrentProject() {
      this.$router.push(
        {
          name: 'project',
          params: {
            project_id: parseInt(this.$route.params.project_id, 10),
          },
        },
      );
    },
  },
};
</script>
